// The hub's quick event finder: one bar of filters that opens the events
// list with them applied.
import { useId, useState } from 'react'
import { useNavigate } from 'react-router'
import { Search } from 'lucide-react'
import { PLATFORMS, STAGE_FORMATS, STAGE_FORMAT_LABELS } from '@pred-hub/shared/constants'
import { Button } from '../ui/Button.jsx'
import { Card } from '../ui/primitives.jsx'
import { platformLabel } from '../../lib/format.js'
import { useRegions } from '../../lib/regions.js'

export const PRIZE_OPTIONS = [
  { value: '1', label: 'With a prize pool' },
  { value: '500', label: '500+ prize pool' },
  { value: '1000', label: '1,000+ prize pool' },
  { value: '5000', label: '5,000+ prize pool' },
]

const ymd = (date) => date.toISOString().slice(0, 10)

// Date presets as from/to days (UTC), starting today.
export function datePreset(preset, now = new Date()) {
  const days = { week: 7, month: 31, quarter: 92 }[preset]
  if (!days) return { from: '', to: '' }
  return { from: ymd(now), to: ymd(new Date(now.getTime() + days * 86_400_000)) }
}

// A filter with its label inside the box, over the choice. It opens as the
// site's dark menu (see .ui-select in app.css).
function Choice({ label, value, onChange, children }) {
  const id = useId()
  return (
    <div className="relative min-w-0">
      <label htmlFor={id} className="pointer-events-none absolute top-1.5 left-3 z-10 text-[10px] font-medium text-ink-muted">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="ui-select field-focus h-12 w-full truncate rounded-lg bg-surface-2 pt-4 pr-8 pl-3 text-sm supports-[appearance:base-select]:pr-3 text-ink ring-1 ring-white/10 ring-inset hover:ring-white/20"
      >
        {children}
      </select>
    </div>
  )
}

export function EventFinder() {
  const navigate = useNavigate()
  const regions = useRegions()
  const [values, setValues] = useState({ status: '', region: '', platform: '', format: '', prize: '', when: '' })
  const set = (key) => (value) => setValues((v) => ({ ...v, [key]: value }))

  function submit(e) {
    e.preventDefault()
    const { when, ...rest } = values
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries({ ...rest, ...datePreset(when) })) if (value) params.set(key, value)
    navigate(`/events${params.size ? `?${params}` : ''}`)
  }

  return (
    <Card as="form" onSubmit={submit} className="grid gap-3 p-3 sm:p-4 xl:grid-cols-[auto_minmax(0,1fr)] xl:items-center xl:gap-5" aria-labelledby="finder-title">
      <h2 id="finder-title" className="flex items-center gap-2.5 px-1 text-[11px] leading-snug font-semibold tracking-[0.12em] text-ink uppercase xl:w-40">
        <Search aria-hidden="true" className="size-4 shrink-0 text-ink-muted" /> Find your next tournament
      </h2>
      <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-[minmax(0,1.35fr)_repeat(5,minmax(0,1fr))_auto] xl:items-center">
        <Choice label="Status" value={values.status} onChange={set('status')}>
          <option value="">Upcoming & live</option>
          <option value="open">Registration open</option>
          <option value="live">Live now</option>
          <option value="completed">Finished</option>
        </Choice>
        <Choice label="Region" value={values.region} onChange={set('region')}>
          <option value="">Any region</option>
          {regions.map((r) => (
            <option key={r.code} value={r.code}>
              {r.name}
            </option>
          ))}
        </Choice>
        <Choice label="Platform" value={values.platform} onChange={set('platform')}>
          <option value="">Any platform</option>
          {PLATFORMS.map((p) => (
            <option key={p} value={p}>
              {platformLabel(p)}
            </option>
          ))}
        </Choice>
        <Choice label="Format" value={values.format} onChange={set('format')}>
          <option value="">Any format</option>
          {STAGE_FORMATS.map((f) => (
            <option key={f} value={f}>
              {STAGE_FORMAT_LABELS[f]}
            </option>
          ))}
        </Choice>
        <Choice label="Prize pool" value={values.prize} onChange={set('prize')}>
          <option value="">Any</option>
          {PRIZE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Choice>
        <Choice label="Starting" value={values.when} onChange={set('when')}>
          <option value="">Any time</option>
          <option value="week">In the next week</option>
          <option value="month">In the next month</option>
          <option value="quarter">In the next 3 months</option>
        </Choice>
        <Button type="submit" className="col-span-2 h-12 md:col-span-3 xl:col-span-1 xl:px-6">
          <Search aria-hidden="true" className="size-4" /> Find events
        </Button>
      </div>
    </Card>
  )
}
